import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { getCategories } from '../../utils/api';
import { FiShoppingCart, FiUser, FiHeart, FiMenu, FiX, FiSearch, FiPackage, FiLogOut, FiSettings, FiChevronDown, FiHome } from 'react-icons/fi';
import styles from './Navbar.module.css';

export default function Navbar() {
  const { user, logout }  = useAuth();
  const { itemCount, subtotal } = useCart();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [mobileOpen, setMobileOpen]     = useState(false);
  const [userMenu, setUserMenu]         = useState(false);
  const [search, setSearch]             = useState('');
  const [scrolled, setScrolled]         = useState(false);
  const [categories, setCategories]     = useState([]);
  const userRef = useRef();

  useEffect(() => {
    getCategories().then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const fn = e => { if (userRef.current && !userRef.current.contains(e.target)) setUserMenu(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  useEffect(() => { setMobileOpen(false); setUserMenu(false); }, [location.pathname]);
  useEffect(() => { document.body.style.overflow = mobileOpen ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [mobileOpen]);

  const handleSearch = e => {
    e.preventDefault();
    if (search.trim()) { navigate(`/products?search=${encodeURIComponent(search.trim())}`); setSearch(''); }
  };

  return (
    <>
      <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`} role="banner">
        {/* Top strip */}
        <div className={styles.strip}>
          <div className={styles.stripInner}>
            <span>🇧🇩 বাংলাদেশের নিজস্ব ই-কমার্স</span>
            <span>•</span>
            <span>Free delivery inside Dhaka on orders ৳999+</span>
            <span>•</span>
            <span>bKash · Nagad · Rocket · COD</span>
          </div>
        </div>

        {/* Main nav */}
        <nav className={styles.nav}>
          <div className={`container ${styles.navInner}`}>
            <Link to="/" className={styles.logo} aria-label="BDShop">
              <span aria-hidden="true">🛒</span>
              <span>BD<strong>Shop</strong></span>
            </Link>

            <form className={styles.searchForm} onSubmit={handleSearch} role="search">
              <div className={styles.searchWrap}>
                <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products, brands…" className={styles.searchInput} aria-label="Search"/>
                <button type="submit" className={styles.searchBtn} aria-label="Search"><FiSearch size={17}/></button>
              </div>
            </form>

            <div className={styles.actions}>
              {user ? (
                <div className={styles.userWrap} ref={userRef}>
                  <button className={styles.userBtn} onClick={() => setUserMenu(o => !o)} aria-expanded={userMenu}>
                    <div className={styles.avatar}>{user.avatar ? <img src={user.avatar} alt=""/> : user.name[0].toUpperCase()}</div>
                    <div className={styles.userMeta}>
                      <span className={styles.userHi}>Hello,</span>
                      <span className={styles.userName}>{user.name.split(' ')[0]}</span>
                    </div>
                    <FiChevronDown size={12} className={`${styles.chev} ${userMenu ? styles.chevOpen : ''}`}/>
                  </button>
                  {userMenu && (
                    <div className={styles.dropdown} role="menu">
                      <div className={styles.ddTop}><p>{user.name}</p><p>{user.email}</p></div>
                      {[
                        { to:'/profile',  icon:FiUser,    label:'My Profile' },
                        { to:'/orders',   icon:FiPackage, label:'My Orders' },
                        { to:'/wishlist', icon:FiHeart,   label:'Wishlist' },
                      ].map(item => (
                        <Link key={item.to} to={item.to} className={styles.ddItem} role="menuitem">
                          <item.icon size={14}/> {item.label}
                        </Link>
                      ))}
                      {['editor','moderator','admin'].includes(user.role) && (
                        <Link to="/admin" className={styles.ddItem} role="menuitem"><FiSettings size={14}/> Admin Panel</Link>
                      )}
                      <div className={styles.ddDiv}/>
                      <button className={`${styles.ddItem} ${styles.ddLogout}`} onClick={() => { logout(); navigate('/'); }} role="menuitem">
                        <FiLogOut size={14}/> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login" className={`btn btn-primary btn-sm ${styles.loginBtn}`}><FiUser size={13}/> Login</Link>
              )}

              <Link to="/wishlist" className={styles.iconBtn} aria-label="Wishlist"><FiHeart size={19}/></Link>

              <Link to="/cart" className={styles.cartBtn} aria-label={`Cart ${itemCount} items`}>
                <FiShoppingCart size={21}/>
                {itemCount > 0 && <span className={styles.cartBadge}>{itemCount > 99 ? '99+' : itemCount}</span>}
                {subtotal > 0 && <span className={styles.cartAmt}>৳{subtotal.toLocaleString()}</span>}
              </Link>

              <button className={styles.hamburger} onClick={() => setMobileOpen(o => !o)} aria-label="Menu" aria-expanded={mobileOpen}>
                {mobileOpen ? <FiX size={22}/> : <FiMenu size={22}/>}
              </button>
            </div>
          </div>
        </nav>

        {/* Category nav */}
        <div className={styles.catBar} role="navigation" aria-label="Product categories">
          <div className={`container ${styles.catList}`}>
            {categories.map(cat => (
              <Link key={cat._id} to={`/products?category=${cat._id}`} className={styles.catLink}>
                {cat.icon} {cat.name}
              </Link>
            ))}
            <Link to="/products?featured=true" className={styles.catLink}>🔥 Featured</Link>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && <div className={styles.overlay} onClick={() => setMobileOpen(false)} aria-hidden="true"/>}

      {/* Mobile drawer */}
      <div className={`${styles.drawer} ${mobileOpen ? styles.drawerOpen : ''}`} role="dialog" aria-modal="true" aria-label="Menu">
        <div className={styles.drawerTop}>
          <span className={styles.logo}>🛒 BD<strong>Shop</strong></span>
          <button onClick={() => setMobileOpen(false)} aria-label="Close"><FiX size={22}/></button>
        </div>

        <form className={styles.mSearch} onSubmit={e => { handleSearch(e); setMobileOpen(false); }}>
          <div className={styles.searchWrap}>
            <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" className={styles.searchInput}/>
            <button type="submit" className={styles.searchBtn}><FiSearch size={15}/></button>
          </div>
        </form>

        <nav className={styles.mNav}>
          <Link to="/"         className={styles.mNavItem}><FiHome size={15}/> Home</Link>
          <Link to="/products" className={styles.mNavItem}><FiShoppingCart size={15}/> All Products</Link>
          <Link to="/cart"     className={styles.mNavItem}>
            <FiShoppingCart size={15}/> Cart
            {itemCount > 0 && <span className={styles.mBadge}>{itemCount}</span>}
          </Link>
          {user ? (
            <>
              <Link to="/profile"  className={styles.mNavItem}><FiUser size={15}/> My Profile</Link>
              <Link to="/orders"   className={styles.mNavItem}><FiPackage size={15}/> My Orders</Link>
              <Link to="/wishlist" className={styles.mNavItem}><FiHeart size={15}/> Wishlist</Link>
              {['editor','moderator','admin'].includes(user.role) && <Link to="/admin" className={styles.mNavItem}><FiSettings size={15}/> Admin Panel</Link>}
              <button className={`${styles.mNavItem} ${styles.mLogout}`} onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}>
                <FiLogOut size={15}/> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login"    className={styles.mNavItem}><FiUser size={15}/> Login</Link>
              <Link to="/register" className={styles.mNavItem}>📝 Register</Link>
            </>
          )}
        </nav>

        <div className={styles.mCats}>
          <p className={styles.mCatsTitle}>Categories</p>
          <div className={styles.mCatGrid}>
            {categories.map(cat => (
              <Link key={cat._id} to={`/products?category=${cat._id}`} className={styles.mCatItem} onClick={() => setMobileOpen(false)}>
                {cat.icon} {cat.name}
              </Link>
            ))}
            <Link to="/products?featured=true" className={styles.mCatItem} onClick={() => setMobileOpen(false)}>🔥 Featured</Link>
          </div>
        </div>
      </div>
    </>
  );
}
